import { useState, type ReactNode } from 'react';
import { AISuggestedBadge, type AIConfidence } from './AISuggestedBadge';
import { cn } from '../../../lib/utils';

export interface AIPrefilledFieldProps {
  /** Confidence of the AI suggestion. Determines whether the badge is shown. */
  confidence?: AIConfidence;
  /** Field children (input, picker, etc.). */
  children: ReactNode;
  /** Called once the user touches the field — caller should clear AI metadata. */
  onConfirm?: () => void;
  /** Override badge label. */
  badgeLabel?: string;
  /** Override badge tooltip. */
  badgeTooltip?: string;
  /** Wrapper className. */
  className?: string;
  /** Badge className. */
  badgeClassName?: string;
}

/**
 * Wraps a form field and shows an AI suggested badge alongside it.
 *
 * - High confidence: no badge (silent).
 * - Medium confidence: neutral badge.
 * - Low confidence: amber badge (caller may also choose to leave the input empty
 *   with just a hint — see /design-system showcase).
 *
 * On the first user-interaction inside the field (click/focus/keydown), the badge
 * is removed and `onConfirm` is invoked once — the user has acknowledged/edited
 * the suggestion, so it's no longer "suggested".
 */
export function AIPrefilledField({
  confidence = 'medium',
  children,
  onConfirm,
  badgeLabel,
  badgeTooltip,
  className,
  badgeClassName,
}: AIPrefilledFieldProps) {
  const [touched, setTouched] = useState(false);

  const handleInteraction = () => {
    if (touched) return;
    setTouched(true);
    onConfirm?.();
  };

  const showBadge = !touched && confidence !== 'high';

  return (
    <div
      className={cn('flex items-start gap-2', className)}
      onPointerDownCapture={handleInteraction}
      onKeyDownCapture={handleInteraction}
      onFocusCapture={handleInteraction}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {showBadge && (
        <AISuggestedBadge
          confidence={confidence}
          label={badgeLabel}
          tooltip={badgeTooltip}
          className={cn('mt-1', badgeClassName)}
        />
      )}
    </div>
  );
}
